/**
 * ¿Esta dosis es disparatada? Aviso, NO bloqueo (auditoría del perito, A12 ·
 * T02-02 + T05-14, 26-09-2026).
 *
 * Se guardaban recetas con 100 L/ha de VERTIMEC (la etiqueta dice 0,8-1 L/ha) y
 * tareas con 30 kg/ha de Switch sin ningún aviso: la tarea proponía 17,38 L para
 * 0,17 ha. Hasta que la app tenga los usos autorizados del registro MAPA por
 * cultivo y plaga (T14-03, fuera de esta tanda), se pide confirmación cuando:
 *
 *   1. la dosis supera un TOPE por tipo de unidad, que ningún fitosanitario
 *      normal alcanza (y que deja pasar los azufres y aceites más cargados con
 *      solo una confirmación), o
 *   2. es más de 3 veces la dosis habitual que el propio producto tiene
 *      guardada en el inventario (`default_dose_value/unit`).
 *
 * Nunca bloquea: la última palabra es de quien receta.
 */

/** Tope por unidad de dosis. Por encima, se pregunta. */
export const DOSE_CAPS: Record<string, number> = {
  "L/ha": 20,
  "kg/ha": 20,
  "L/1000m²": 2,
  "kg/1000m²": 2,
  "mL/100L": 2000,
  "g/100L": 2000,
};

/** Veces por encima de la dosis habitual del producto a partir de las que se pregunta. */
export const DEFAULT_DOSE_FACTOR = 3;

export interface DoseLine {
  product_name: string;
  rate_value: number;
  rate_unit: string;
  /** Dosis habitual guardada en el producto (inventario), si la hay. */
  default_dose_value?: number | null;
  default_dose_unit?: string | null;
}

export interface DoseWarning {
  product_name: string;
  rate_value: number;
  rate_unit: string;
  reason: "over_cap" | "over_default";
  /** El tope superado (over_cap) o la dosis habitual (over_default), en `limit_unit`. */
  limit: number;
  limit_unit: string;
}

/**
 * Una dosis por superficie o por caldo, llevada a una base común para poder
 * comparar: por hectárea (L/ha, kg/ha) o por 100 L de caldo (mL/100L, g/100L).
 * Devuelve null para unidades absolutas (L, kg…) o desconocidas.
 */
export function toComparableDose(
  value: number,
  unit: string | null | undefined,
): { value: number; unit: string } | null {
  const u = (unit || "").replace(/\s/g, "").replace("m2", "m²").toLowerCase();
  switch (u) {
    case "l/ha":
      return { value, unit: "L/ha" };
    case "kg/ha":
      return { value, unit: "kg/ha" };
    case "ml/ha":
      return { value: value / 1000, unit: "L/ha" };
    case "g/ha":
      return { value: value / 1000, unit: "kg/ha" };
    case "l/1000m²":
      return { value: value * 10, unit: "L/ha" };
    case "kg/1000m²":
      return { value: value * 10, unit: "kg/ha" };
    case "ml/100l":
    case "cc/100l":
      return { value, unit: "mL/100L" };
    case "g/100l":
      return { value, unit: "g/100L" };
    case "l/100l":
      return { value: value * 1000, unit: "mL/100L" };
    case "kg/100l":
      return { value: value * 1000, unit: "g/100L" };
    default:
      return null;
  }
}

/** Los avisos de un conjunto de líneas (receta o tarea). Vacío = nada que preguntar. */
export function doseWarnings(lines: readonly DoseLine[]): DoseWarning[] {
  const out: DoseWarning[] = [];
  for (const line of lines) {
    if (!(line.rate_value > 0)) continue; // el 0 lo para otra regla
    const cap = DOSE_CAPS[line.rate_unit];
    if (cap != null && line.rate_value > cap) {
      out.push({
        product_name: line.product_name,
        rate_value: line.rate_value,
        rate_unit: line.rate_unit,
        reason: "over_cap",
        limit: cap,
        limit_unit: line.rate_unit,
      });
      continue;
    }
    const mine = toComparableDose(line.rate_value, line.rate_unit);
    const usual =
      line.default_dose_value != null && line.default_dose_value > 0
        ? toComparableDose(line.default_dose_value, line.default_dose_unit)
        : null;
    if (mine && usual && mine.unit === usual.unit && mine.value > usual.value * DEFAULT_DOSE_FACTOR) {
      out.push({
        product_name: line.product_name,
        rate_value: line.rate_value,
        rate_unit: line.rate_unit,
        reason: "over_default",
        limit: line.default_dose_value as number,
        limit_unit: line.default_dose_unit as string,
      });
    }
  }
  return out;
}
